import MuiTextField, { TextFieldProps } from '@mui/material/TextField';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useState, useEffect, useCallback } from 'react';

/**
 * Parses a localized number string to a float
 * Accepts both comma and period as decimal separators
 */
export function parseLocalizedNumber(value: string): number | null {
    if (!value || value.trim() === '') return null;

    // Replace comma with period for parsing
    const normalizedValue = value.replace(',', '.');

    const parsed = parseFloat(normalizedValue);

    return isNaN(parsed) ? null : parsed;
}

/**
 * Formats a number for display using the current locale
 */
export function formatNumberForDisplay(
    value: number | null | undefined,
    locale: string,
    allowDecimals: boolean
): string {
    if (value === null || value === undefined) return '';

    // For locale formatting, we need to determine decimal separator
    const formatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: allowDecimals ? 10 : 0,
        useGrouping: false,
    });

    return formatter.format(value);
}

interface INumberInputProps {
    field: ControllerRenderProps<FieldValues, string>;
    error?: string;
    locale: string;
    allowDecimals: boolean;
    textFieldProps: Omit<
        TextFieldProps,
        'name' | 'value' | 'onChange' | 'onBlur'
    >;
}

export const NumberInput = ({
    field,
    error,
    locale,
    allowDecimals,
    textFieldProps,
}: INumberInputProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [displayValue, setDisplayValue] = useState('');

    // Update display value when field value changes externally
    useEffect(() => {
        if (!isEditing) {
            setDisplayValue(
                formatNumberForDisplay(
                    field.value as number | null | undefined,
                    locale,
                    allowDecimals
                )
            );
        }
    }, [field.value, isEditing, locale, allowDecimals]);

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            // Parse and set the value as a number on blur
            const parsedValue = parseLocalizedNumber(e.target.value);
            field.onChange(parsedValue);
            field.onBlur();
            setIsEditing(false);
        },
        [field]
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setDisplayValue(e.target.value);
        },
        []
    );

    const handleFocus = useCallback(() => {
        setIsEditing(true);
    }, []);

    return (
        <MuiTextField
            {...textFieldProps}
            fullWidth
            error={!!error}
            helperText={error}
            margin="normal"
            value={displayValue}
            variant="outlined"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
        />
    );
};
